import { Injectable, inject, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { AuthService } from '@portal/app/shared/services/auth.service';

/**
 * Self-contained context service for the MIA panel.
 *
 * Mirrors the pattern used by geo-designer-v2's QueryParamsService:
 * clientId and brandId come from the global SelectionService signal;
 * dashboardLiteralId is parsed from the current Router URL on every
 * NavigationEnd so the panel's panel-config query key updates automatically
 * when the user navigates between pages. The panel can be hosted in any
 * shell and is self-sufficient — no @Input() wiring required.
 */
@Injectable({ providedIn: 'root' })
export class MiaContextService {
  private readonly selectionService = inject(SelectionService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly clientId = computed(
    () => this.selectionService.clientBrandSignal().clientId
  );

  readonly brandId = computed(
    () => this.selectionService.clientBrandSignal().brandId
  );

  /**
   * Master `product_dashboard.literal_id` for the current page.
   *
   * The canonical dashboard URL is `/products/<productId>/<literalId>`
   * (see `smart-redirect.component.ts` and the dashboard router config).
   * The literal_id is the THIRD path component after `/products/`, not
   * the first — the segment immediately after `/products/` is the
   * product-catalog group (e.g. `portfolio`, `mpo`, `mim`), which is
   * NOT a literal_id and must not be used for agent page-scope
   * matching.
   *
   * Examples:
   *   /a/<tenant>/products/portfolio/cross-channel      → "cross-channel"
   *   /a/<tenant>/products/portfolio/home-page          → "home-page"
   *   /a/<tenant>/products/portfolio/optimization-report → "optimization-report"
   *   /a/<tenant>/products/mpo/mpo-plans                 → "mpo-plans"
   *   /a/<tenant>/products/measured-benchmarks/measured-benchmarks
   *                                                      → "measured-benchmarks"
   *
   * Non-dashboard product paths (admin, settings, geo-designer routes)
   * have no `<literalId>` segment and fall through to empty string —
   * the panel then shows only generic (page-unscoped) agents, which is
   * the right behavior for non-dashboard pages.
   *
   * The initialValue is read synchronously from router.url so the
   * signal is populated before the first NavigationEnd fires.
   */
  readonly dashboardLiteralId = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => MiaContextService.parseLiteralId(e.urlAfterRedirects))
    ),
    { initialValue: MiaContextService.parseLiteralId(this.router.url) }
  );

  /**
   * Display name for the current user (shown in the panel welcome heading).
   * Falls back to empty string when auth is not yet resolved.
   */
  readonly userName = computed(
    () => this.authService.getUser()?.getName() ?? ''
  );

  /**
   * True once we have a valid tenant context — gates all panel queries.
   */
  readonly isReady = computed(() => this.clientId() > 0 && this.brandId() > 0);

  private static parseLiteralId(url: string): string {
    // Matches `/products/<productId>/<literalId>` and captures the
    // literal_id (segment 3). Single-segment paths under /products/
    // (admin, settings-manager, geo-designer-v2, etc.) intentionally
    // do not match — those pages are not dashboards and should
    // resolve to empty string.
    const match = url.match(/\/products\/[^/?#]+\/([^/?#]+)/);
    return match ? (match[1] ?? '') : '';
  }
}
